import { useEffect, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InvoiceAPI } from "@/lib/api";

/**
 * InvoicePDFPreview
 * - The /invoices/{id}/pdf endpoint is Bearer-auth protected, so a plain
 *   <iframe src>/<a href> navigation 401s. We fetch the PDF as an authenticated
 *   blob and render the object URL instead.
 * - Provides an authenticated download button.
 */
export function InvoicePDFPreview({ invoiceId, status, downloadName = "invoice.pdf" }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [errored, setErrored] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const isDraft = !invoiceId || status === "draft";

    useEffect(() => {
        if (isDraft) return undefined;
        let revoke = null;
        let cancelled = false;
        InvoiceAPI.pdfBlobUrl(invoiceId)
            .then((u) => {
                if (cancelled) { window.URL.revokeObjectURL(u); return; }
                revoke = u;
                setBlobUrl(u);
            })
            .catch(() => { if (!cancelled) setErrored(true); });
        return () => {
            cancelled = true;
            if (revoke) window.URL.revokeObjectURL(revoke);
        };
    }, [invoiceId, status, isDraft]);

    const download = async () => {
        setDownloading(true);
        try {
            await InvoiceAPI.downloadPdf(invoiceId, downloadName);
        } catch {
            toast.error("Failed to download PDF");
        } finally {
            setDownloading(false);
        }
    };

    if (isDraft) return (
        <Card className="grid h-[420px] place-items-center text-sm text-zinc-500" data-testid="invoice-pdf-empty">
            <span className="inline-flex items-center gap-2"><FileText className="h-4 w-4" /> Issue the invoice to generate a PDF</span>
        </Card>
    );

    return (
        <Card className="overflow-hidden" data-testid="invoice-pdf-preview">
            <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-2 dark:border-zinc-800">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Invoice PDF</div>
                <Button size="sm" variant="outline" onClick={download} disabled={downloading} data-testid="invoice-pdf-download">
                    {downloading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Download className="mr-1 h-3 w-3" />}
                    Download
                </Button>
            </div>
            <div className="relative">
                {!blobUrl && !errored && (
                    <div className="absolute inset-0 grid h-[420px] place-items-center bg-zinc-50 dark:bg-zinc-900">
                        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
                    </div>
                )}
                {blobUrl && (
                    <iframe
                        title="invoice-pdf"
                        src={blobUrl}
                        data-testid="invoice-pdf-iframe"
                        className="h-[420px] w-full"
                    />
                )}
                {errored && (
                    <div className="grid h-[420px] place-items-center bg-zinc-50 p-6 text-center text-sm text-zinc-600 dark:bg-zinc-900 dark:text-zinc-400">
                        Could not load the PDF preview.{" "}
                        <button onClick={download} className="ml-1 text-brand-redDeep underline dark:text-brand-red">Download instead →</button>
                    </div>
                )}
            </div>
        </Card>
    );
}
